import React, { useMemo, useState } from 'react';
import { Percent, Check, X, Clock } from 'lucide-react';

/**
 * ⛔⛔ THE COMMISSION PERCENTAGE, TYPED BY A HUMAN — Card
 * PARTNER-MANUAL-COMMISSION.
 *
 * Ryland, via Daniel, Thu 8 Oct 2026: "Change the tier section to a commission
 * percentage I manually input." Daniel, same day: ONE percentage per partner
 * COMPANY. This panel is what replaced the tier dropdown on the partner's
 * Overview tab.
 *
 * ── ⚠⚠ THE MIRRORED VALIDATOR ──────────────────────────────────────────────
 *
 * The real gate is the SERVER — database/partnerCommissionRate.js, reached
 * through alliance_partner:orgs:set-commission-rate, which re-validates
 * everything below and is the only thing that decides what lands in the
 * database. This copy exists so staff see the problem as they type rather than
 * after a round trip, and for no other reason.
 *
 * ⛔ It is COPIED, not imported: Create React App's module scope forbids
 * importing from outside src/, and database/ is server code. The two literals
 * are held together by a source-level assertion in
 * tests/unit/security/partnerCommissionRate.test.js, which reads BOTH
 * files and compares the patterns — a "keep these in sync" comment is not a
 * control, and this repo has already been bitten by one that was true when it
 * was written and false six weeks later.
 *
 * ── ⛔ WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────
 *
 * NO RETROACTIVE CHANGE — Daniel's hard constraint. There is no "apply from"
 * date in the past, and the confirmation says in words that commission already
 * earned keeps the rate it was earned at. That is not a promise the UI is
 * keeping on its own: the earn sweeper stamps rate_bps onto every ledger row at
 * the moment it is earned, so the property holds structurally and this copy
 * merely tells the truth about it.
 */

/** ⚠ MIRRORS database/partnerCommissionRate.js's PCT_RE. Asserted in tests. */
export const PCT_RE = /^(\d{1,3})(?:\.(\d{1,2}))?$/;

/** ⚠ MIRRORS that module's RATE_ERROR_TEXT keys. Asserted in tests. */
export const RATE_ERROR_TEXT = {
  RATE_NOT_A_NUMBER: 'Enter a number, like 12.5 — digits and one decimal point only.',
  RATE_NEGATIVE: 'A commission rate cannot be negative.',
  RATE_TOO_MANY_DECIMALS: 'Use at most two decimal places, like 12.34.',
  RATE_OUT_OF_RANGE: 'Enter a rate between 0 and 100.',
  EFFECTIVE_FROM_INVALID: 'That date could not be read.',
  EFFECTIVE_FROM_RETROACTIVE:
    'A rate change cannot be backdated. Commission already earned keeps the rate it was earned at.',
  EFFECTIVE_FROM_NOT_ACCEPTED:
    'A rate change takes effect immediately and cannot be scheduled for a later date. '
    + 'Save it on the day it should start.',
};

/** The client-side half of the same rule. Server re-checks all of it. */
export function validateRatePct(raw) {
  if (raw === null || raw === undefined) return { ok: true, pct: null };
  const text = String(raw).trim();
  if (text === '') return { ok: true, pct: null };
  if (text.startsWith('-')) return { ok: false, code: 'RATE_NEGATIVE' };
  if (!PCT_RE.test(text)) {
    if (/^\d{1,3}(\.\d+)?$/.test(text)) return { ok: false, code: 'RATE_TOO_MANY_DECIMALS' };
    return { ok: false, code: 'RATE_NOT_A_NUMBER' };
  }
  const pct = Number(text);
  if (!Number.isFinite(pct)) return { ok: false, code: 'RATE_NOT_A_NUMBER' };
  if (pct < 0 || pct > 100) return { ok: false, code: 'RATE_OUT_OF_RANGE' };
  return { ok: true, pct };
}

/**
 * ⚠ "not set" and "0%" render DIFFERENTLY and that is load-bearing.
 *
 * A partner nobody has configured and a partner deliberately placed on zero are
 * different facts with different fixes, and an em dash for both would make the
 * first invisible — which is precisely the state the engine HOLDS accruals for.
 */
export function formatPct(pct) {
  if (pct === null || pct === undefined || pct === '') return null;
  const n = Number(pct);
  if (!Number.isFinite(n)) return null;
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2).replace(/0$/, '')}%`;
}

export default function CommissionRatePanel({
  org,
  history = [],
  canEdit,
  busy,
  onSave,
  testId = 'commission-rate',
}) {
  const current = org && org.commission_rate_pct != null ? Number(org.commission_rate_pct) : null;
  const [draft, setDraft] = useState(current == null ? '' : String(current));
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const check = useMemo(() => validateRatePct(draft), [draft]);
  const nextPct = check.ok ? check.pct : null;
  const changed = check.ok && nextPct !== current;

  function submit() {
    if (!canEdit || busy || !check.ok || !changed) return;
    onSave({ ratePct: draft.trim() === '' ? null : draft.trim(), reason: reason.trim() || null });
    setConfirming(false);
    setReason('');
  }

  return (
    <div data-testid={testId} style={{ gridColumn: '1/-1' }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>
        <Percent size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        Commission rate
      </label>

      {/*
        ⛔ THE CURRENT VALUE IS ALWAYS SHOWN, EDITABLE OR NOT. A reader without
        the permission still needs to know what this partner is on — hiding the
        number because they cannot change it is a different and worse control.
      */}
      <div data-testid={`${testId}-current`} style={{ fontSize: 20, fontWeight: 700, color: current == null ? '#DC3545' : '#212529' }}>
        {current == null ? 'Not set' : formatPct(current)}
      </div>
      {current == null && (
        <div data-testid={`${testId}-held`} style={{ fontSize: 11, color: '#DC3545', marginTop: 2, fontWeight: 600 }}>
          {/*
            ⛔⛔ THE HOLD, SAID OUT LOUD. The engine already refused to invent a
            rate — it counted the skip in a run-log statistic nobody opens. This
            line is that same fact put where the person who can fix it will see
            it. "Not set" on its own reads as a cosmetic blank.
          */}
          No commission is being calculated for this partner. Therapies they refer
          are held — not lost — and will accrue at the rate entered here once it is set.
        </div>
      )}

      {canEdit ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <input
                data-testid={`${testId}-input`}
                aria-label="Commission rate percent"
                value={draft}
                disabled={busy}
                inputMode="decimal"
                placeholder="e.g. 12.5"
                onChange={(e) => { setDraft(e.target.value); setConfirming(false); }}
                style={{
                  width: 110, padding: '6px 8px', fontSize: 14, borderRadius: 6,
                  border: `1px solid ${check.ok ? '#CED4DA' : '#DC3545'}`,
                  boxSizing: 'border-box',
                }}
              />
              <span style={{ marginLeft: 6, fontSize: 13, color: '#6C757D' }}>%</span>
            </div>
            {!confirming && (
              <button
                data-testid={`${testId}-save`}
                disabled={busy || !check.ok || !changed}
                onClick={() => setConfirming(true)}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                  border: 'none', color: 'white',
                  background: (!check.ok || !changed || busy) ? '#ADB5BD' : '#2E8B98',
                  cursor: (!check.ok || !changed || busy) ? 'not-allowed' : 'pointer',
                }}
              >
                Save rate
              </button>
            )}
          </div>

          {!check.ok && (
            <div data-testid={`${testId}-error`} role="alert" style={{ fontSize: 11, color: '#DC3545', marginTop: 4, fontWeight: 600 }}>
              {RATE_ERROR_TEXT[check.code]}
            </div>
          )}

          {/*
            ⛔⛔ THE CONFIRMATION NAMES BOTH NUMBERS AND THE DATE.
            A bare "are you sure?" is a click to get past. Showing OLD → NEW is
            what catches the typo that matters here: 125 where 12.5 was meant is
            a tenfold error that looks entirely ordinary in an input box, and
            the only moment anyone will notice it is when both numbers are on
            screen together.
          */}
          {confirming && check.ok && changed && (
            <div
              data-testid={`${testId}-confirm`}
              role="region"
              aria-label="Confirm commission rate change"
              style={{
                marginTop: 10, padding: 12, borderRadius: 8,
                background: '#FFF8E6', border: '1px solid #F0B429',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
                Change commission rate from{' '}
                <span data-testid={`${testId}-confirm-from`}>{current == null ? 'not set' : formatPct(current)}</span>
                {' → '}
                <span data-testid={`${testId}-confirm-to`}>{nextPct == null ? 'not set' : formatPct(nextPct)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#8A6100', marginTop: 4 }}>
                Effective <strong>now</strong> ({new Date().toLocaleString()}).{' '}
                {/*
                  ⚠ The no-retroactive rule, in words, at the moment it matters.
                  Staff ask "does this fix last month?" and the answer is no.
                */}
                Commission already earned keeps the rate it was earned at — this does not change past accruals.
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginTop: 8, marginBottom: 3 }}>
                  Reason (optional)
                </label>
                <input
                  data-testid={`${testId}-reason`}
                  aria-label="Reason for the rate change"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. renegotiated contract, Q4"
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #CED4DA', boxSizing: 'border-box' }}
                />
                {/*
                  ⛔ No PHI in a commission record. Said here, where it is typed.
                */}
                <div style={{ fontSize: 10, color: '#ADB5BD', marginTop: 3 }}>
                  Describe the business decision. Never a patient or a referral.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  data-testid={`${testId}-confirm-save`}
                  disabled={busy}
                  onClick={submit}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6,
                    border: 'none', background: '#2E8B98', color: 'white', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <Check size={12} /> Confirm new rate
                </button>
                <button
                  data-testid={`${testId}-cancel`}
                  onClick={() => setConfirming(false)}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                    border: '1px solid #CED4DA', background: 'white', color: '#6C757D', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <X size={12} /> Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /*
          ⚠ The read-only case says WHY it is read-only. "No button" is
          indistinguishable from "the page is broken" to someone who expected
          one — and this page has shipped a blank-where-a-control-should-be bug
          before.
        */
        <div data-testid={`${testId}-readonly`} style={{ fontSize: 11, color: '#ADB5BD', marginTop: 6 }}>
          Only an administrator can change the commission rate.
        </div>
      )}

      {/* ─── History: never edited, never deleted ───────────────────────── */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6C757D', marginBottom: 6 }}>
          <Clock size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Rate history
        </div>
        {history.length === 0 ? (
          <div data-testid={`${testId}-history-empty`} style={{ fontSize: 11, color: '#ADB5BD' }}>
            No rate changes recorded.
          </div>
        ) : (
          <div className="responsive-scroll">
            <table data-testid={`${testId}-history`} style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6C757D' }}>
                  <th style={{ padding: '4px 6px' }}>Effective</th>
                  <th style={{ padding: '4px 6px' }}>From</th>
                  <th style={{ padding: '4px 6px' }}>To</th>
                  <th style={{ padding: '4px 6px' }}>Changed by</th>
                  <th style={{ padding: '4px 6px' }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} style={{ borderTop: '1px solid #E9ECEF' }}>
                    <td style={{ padding: '4px 6px' }}>{new Date(h.effective_from).toLocaleString()}</td>
                    <td style={{ padding: '4px 6px' }}>{h.old_rate == null ? 'not set' : formatPct(h.old_rate)}</td>
                    <td style={{ padding: '4px 6px', fontWeight: 700 }}>{h.new_rate == null ? 'not set' : formatPct(h.new_rate)}</td>
                    <td style={{ padding: '4px 6px' }}>{h.changed_by_name || '—'}</td>
                    <td style={{ padding: '4px 6px', color: '#6C757D' }}>{h.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
